/**
 * Readiness badge for board job cards. Labels come from shared-core
 * (`@shared-core/domain/readiness`); the visual variant (colour) per value
 * is a shell concern, so it lives here — same split as
 * `domain/issues/issueBadges.tsx`. Colours are deliberately outside the
 * board's red/amber/green deadline-urgency vocabulary: readiness and
 * deadline urgency are two independent axes and must not borrow each
 * other's colour language (see the `readiness` design tokens in
 * `packages/shared-core/src/tokens/source.ts`).
 */
import type { CSSProperties } from "react";
import { Badge } from "../../primitives/badge";
import { cn } from "../../primitives/utils";
import { readinessLabel } from "@shared-core/domain/readiness";
import type { Readiness } from "@shared-core/data/api-types";

/** Exported so `CommandCentreReadinessStrip`'s proportional bar can colour
 *  its segments with the same ladder palette this badge uses — one colour
 *  mapping, not two independently maintained ones. */
export const READINESS_STYLE: Record<
  Exclude<Readiness, "non_press">,
  CSSProperties
> = {
  not_production_ready: {
    backgroundColor: "var(--readiness-not-production-ready)",
    color: "var(--readiness-not-production-ready-foreground)",
  },
  out_for_proof: {
    backgroundColor: "var(--readiness-out-for-proof)",
    color: "var(--readiness-out-for-proof-foreground)",
  },
  production_ready: {
    backgroundColor: "var(--readiness-production-ready)",
    color: "var(--readiness-production-ready-foreground)",
  },
  in_production: {
    backgroundColor: "var(--readiness-in-production)",
    color: "var(--readiness-in-production-foreground)",
  },
};

/** Whether `ReadinessBadge` would render anything for this readiness/
 *  work_kind pair — `non_press` with no `work_kind` is the one case with
 *  nothing to show. Exported so a caller (`PortfolioBoard`'s gutter) can
 *  decide its own layout — e.g. whether to reserve width for a badge —
 *  without duplicating this rule or rendering the badge just to inspect it. */
export function readinessBadgeVisible(
  readiness: Readiness,
  workKind: string | null,
): boolean {
  return readiness !== "non_press" || workKind != null;
}

/** `non_press` work will never reach the print-readiness ladder, so it shows
 *  its own `work_kind` ("Factored", "Postage", …) instead of a readiness
 *  label that would never apply — a plain neutral badge, not a ladder
 *  colour. Renders nothing if a non_press job somehow carries no work_kind
 *  (see `readinessBadgeVisible`). */
export function ReadinessBadge({
  readiness,
  workKind,
  className,
}: {
  readiness: Readiness;
  workKind: string | null;
  className?: string;
}) {
  if (readiness === "non_press") {
    if (!workKind) return null;
    return (
      <Badge variant="outline" className={className}>
        {workKind}
      </Badge>
    );
  }
  return (
    <Badge
      className={cn("border-transparent", className)}
      style={READINESS_STYLE[readiness]}
    >
      {readinessLabel(readiness)}
    </Badge>
  );
}
